// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { LocationIntegrationStatus } from '@/location/components/location-integration-status';
import {
  getEffectiveAddress,
  getEffectiveLocationName,
} from '@/location/utilities/get-effective-third-party';
import { Shipment } from '@/shipment/api/types';
import { Box, Stack, Typography } from '@mui/material';

interface Props {
  shipment: Pick<
    Shipment,
    'fromLocation' | 'toLocation' | 'expectedShippedOn' | 'expectedDeliveredOn'
  >;
  onDriftResolved: () => void;
}

/**
 * Displays the from/to location information for a shipment.
 */
export function ShipmentLocationInfo(props: Props) {
  const { shipment } = props;

  return (
    <>
      {/* From location */}
      <Box marginTop={4} paddingX={1}>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="column" spacing={0.5}>
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography variant="subtitle2">From:</Typography>
              <Typography variant="body2">
                {shipment.fromLocation
                  ? getEffectiveLocationName(shipment.fromLocation)
                  : 'Not set'}
              </Typography>
              <LocationIntegrationStatus
                location={shipment.fromLocation}
                onResolved={props.onDriftResolved}
              />
            </Stack>
            {shipment.fromLocation &&
              getEffectiveAddress(shipment.fromLocation) && (
                <Typography variant="body2" color="text.secondary">
                  {getEffectiveAddress(shipment.fromLocation)}
                </Typography>
              )}
          </Stack>
          <Stack direction="column" alignItems="flex-end" spacing={0.5}>
            <Typography variant="body2" color="text.secondary">
              Expected ship
            </Typography>
            <Typography
              variant="body2"
              color={
                shipment.expectedShippedOn ? 'text.primary' : 'text.secondary'
              }
            >
              {shipment.expectedShippedOn?.toLocaleString({
                dateStyle: 'medium',
              }) || 'Not set'}
            </Typography>
          </Stack>
        </Stack>
      </Box>

      {/* To location */}
      <Box marginTop={2} paddingX={1}>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="column" spacing={0.5}>
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography variant="subtitle2">To:</Typography>
              <Typography variant="body2">
                {shipment.toLocation
                  ? getEffectiveLocationName(shipment.toLocation)
                  : 'Not set'}
              </Typography>
              <LocationIntegrationStatus
                location={shipment.toLocation}
                onResolved={props.onDriftResolved}
              />
            </Stack>
            {shipment.toLocation &&
              getEffectiveAddress(shipment.toLocation) && (
                <Typography variant="body2" color="text.secondary">
                  {getEffectiveAddress(shipment.toLocation)}
                </Typography>
              )}
          </Stack>
          <Stack direction="column" alignItems="flex-end" spacing={0.5}>
            <Typography variant="body2" color="text.secondary">
              Expected delivery
            </Typography>
            <Typography
              variant="body2"
              color={
                shipment.expectedDeliveredOn ? 'text.primary' : 'text.secondary'
              }
            >
              {shipment.expectedDeliveredOn?.toLocaleString({
                dateStyle: 'medium',
              }) || 'Not set'}
            </Typography>
          </Stack>
        </Stack>
      </Box>
    </>
  );
}
